import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useProjectStore } from '../../contexts/ProjectContext';
import { getXERProjectTasks } from '../../api/schedule';
import { ActivityTable } from '@/components/ActivityTable';
import { ScheduleActivity } from '@/types';
import { projectApi } from '../../api/projectApi';
import MainLayout from '../../components/layout/MainLayout';

const ProjectActivities: React.FC = () => {
  const { projectId } = useParams();
  const { currentProject, projects, refreshProjects } = useProjectStore();
  
  const [activities, setActivities] = useState<ScheduleActivity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localProject, setLocalProject] = useState<any>(null);

  // Get the project from the URL parameter, local state, or current project
  const project = projectId 
    ? localProject || projects.find(p => p.id === projectId) || currentProject
    : currentProject;

  // Load project data if not already loaded
  useEffect(() => {
    const loadProject = async () => {
      if (projectId && !localProject && !projects.find(p => p.id === projectId)) {
        try {
          // Try to get project from API
          const projectData = await projectApi.getProject(projectId);
          if (projectData) {
            // Set the project in local state
            setLocalProject(projectData);
          }
        } catch (error) {
          console.error('Error loading project:', error);
        }
      }
    };

    loadProject();
  }, [projectId, localProject, projects]);

  // Fetch activities when project is loaded
  useEffect(() => {
    if (projectId) {
      fetchActivities();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchActivities recreated each render; projectId is key
  }, [projectId]);

  const fetchActivities = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getXERProjectTasks(projectId);
      // Handle both new format {activities, udf_labels} and old format (array)
      const activitiesData = Array.isArray(response) ? response : response.activities || [];
      setActivities(activitiesData);
    } catch (err) {
      console.error('Error fetching activities:', err);
      setError('Failed to load activities');
    } finally {
      setIsLoading(false);
    }
  };

  if (!project) {
    return (
      <MainLayout title="Project Not Found">
        <div className="p-6">
          <div className="text-center py-12">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-medium text-gray-300 mb-2">Project Not Found</h3>
            <p className="text-gray-400">The requested project could not be found.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={`${project?.name || 'Project'} - Activities`}>
      <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Activities</h1>
        <p className="text-gray-400">
          {project?.name || 'Project'} - XER Tasks
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-900/20 border border-red-700 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-300">Error</h3>
              <div className="mt-2 text-sm text-red-200">{error}</div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-lg shadow-sm border border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-nb-accent-primary"></div>
            <span className="ml-3 text-gray-500">Loading activities...</span>
          </div>
        ) : activities.length > 0 ? (
          <ActivityTable 
            activities={activities}
          />
        ) : (
          <div className="text-center py-12">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="text-lg font-medium text-gray-300 mb-2">No Activities Found</h3>
            <p className="text-gray-400 mb-4">
              This schedule doesn't have any activities loaded yet.
            </p>
            <button
              onClick={fetchActivities}
              className="px-4 py-2 bg-nb-accent-primary hover:bg-nb-accent-blue text-white rounded-md transition-colors"
            >
              Refresh Activities
            </button>
          </div>
        )}
      </div>
      </div>
    </MainLayout>
  );
};

export default ProjectActivities; 

